import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import AudioButton from "../AudioButton";
import Confetti from "../Confetti";
import { speak, speakWord, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

const VOWELS = "aeiou";

export default function MagicWandGame({ level, onFinish }) {
  const pairs = level.pairs;
  const [pi, setPi] = useState(0);
  const [transformed, setTransformed] = useState(false);
  const [casting, setCasting] = useState(false);
  const [burst, setBurst] = useState(false);
  const pair = pairs[pi];

  useEffect(() => {
    preload([
      { text: pair.short, kind: "word" },
      { text: pair.long, kind: "word" },
    ]);
    const t = setTimeout(() => {
      speakWord(pair.short);
      setTimeout(() => speak("Wave the magic wand to add a silent E!"), 900);
    }, 400);
    return () => clearTimeout(t);
  }, [pi]); // eslint-disable-line

  const cast = () => {
    if (transformed || casting) return;
    setCasting(true);
    sfx.magic();
    speak("Abracadabra!");
    setTimeout(() => {
      setTransformed(true);
      setBurst(true);
      speakWord(pair.short);
      setTimeout(() => {
        speakWord(pair.long);
        setTimeout(() => speak(`The magic E makes the vowel say its name! ${pair.long}!`), 900);
      }, 900);
      setTimeout(() => setBurst(false), 1500);
      setTimeout(next, 3600);
    }, 700);
  };

  const next = () => {
    if (pi + 1 >= pairs.length) {
      onFinish({ stars: 3, accuracy: 1 });
    } else {
      setPi((i) => i + 1);
      setTransformed(false);
      setCasting(false);
    }
  };

  const word = transformed ? pair.long : pair.short;
  const baseLetters = pair.short.split("");

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 pb-8 min-h-full no-select">
      <Confetti show={burst} count={60} />

      <div className="flex items-center gap-3 bg-white/95 rounded-3xl border-4 border-pink-300 shadow-lg px-4 py-3 mt-2 max-w-lg">
        <AudioButton text="Wave the magic wand to add a silent E!" testId="prompt-audio" size={56} />
        <p className="font-display text-lg sm:text-xl font-bold text-pink-900" data-testid="game-prompt">
          Tap the word with the wand to add Magic E!
        </p>
      </div>

      {/* Word display */}
      <button
        data-testid="magic-word"
        onClick={cast}
        className="pop-btn relative mt-12 mb-8 flex items-end gap-1 rounded-3xl bg-white border-4 border-pink-200 shadow-[0_8px_0_#F472B6] active:shadow-none px-6 py-6"
      >
        {baseLetters.map((l, i) => (
          <span
            key={i}
            className="font-display text-6xl sm:text-7xl font-black"
            style={{ color: VOWELS.includes(l) ? "#EF4444" : "#3B82F6" }}
          >
            {l}
          </span>
        ))}
        <AnimatePresence>
          {transformed && (
            <motion.span
              initial={{ x: 140, y: -120, scale: 0, rotate: 90, opacity: 0 }}
              animate={{ x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 14 }}
              className="font-display text-6xl sm:text-7xl font-black text-purple-500"
              data-testid="magic-e"
            >
              e
            </motion.span>
          )}
        </AnimatePresence>
        {casting && !transformed && (
          <motion.div
            className="absolute inset-0 rounded-3xl"
            animate={{ boxShadow: ["0 0 0 0 #A855F7", "0 0 40px 10px #A855F7", "0 0 0 0 #A855F7"] }}
            transition={{ duration: 0.7 }}
          />
        )}
      </button>

      {/* Wand (draggable for delight, also tap button) */}
      <motion.button
        data-testid="magic-wand"
        drag
        dragSnapToOrigin
        onDragEnd={cast}
        onClick={cast}
        whileTap={{ scale: 1.1 }}
        animate={{ rotate: [-8, 8, -8] }}
        transition={{ repeat: Infinity, duration: 1.6 }}
        className="pop-btn flex items-center gap-2 rounded-full bg-purple-500 text-white font-display text-xl font-bold px-6 py-4 border-4 border-white shadow-[0_6px_0_#7E22CE] active:shadow-none cursor-grab"
      >
        <Sparkles strokeWidth={2.5} className="w-7 h-7" /> Wave the Wand!
      </motion.button>

      <div className="flex gap-2 mt-8">
        {pairs.map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < pi ? "bg-green-500" : i === pi ? "bg-pink-400 animate-pulse" : "bg-slate-300"}`} />
        ))}
      </div>
    </div>
  );
}
